import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/concrete-sidewalk-installation-wayne-nj.webp";
import baseImage from "@/assets/concrete-sidewalk-prep-totowa-nj.webp";
import formsImage from "@/assets/fresh-concrete-sidewalk-totowa-nj.webp";
import winterPourImage from "@/assets/concrete-sidewalk-poured-totowa-nj.webp";
import finishedImage from "@/assets/concrete-sidewalk-complete-totowa-nj.webp";

// Citation link component for inline references
const CitationLink = ({ num, url }: { num: number; url: string }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary hover:text-primary/80 text-sm align-super ml-0.5"
  >
    [{num}]
  </a>
);

// FAQ Schema for SEO
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does concrete sidewalk installation take in North Jersey?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most residential sidewalks take 2 to 5 days to complete. That includes excavation, base prep, forming, pouring, and cure time. Weather can also play a role, especially if we're working around rain in Bergen or Essex counties."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need a permit for a concrete sidewalk in North New Jersey?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It depends on your municipality. Some towns require a permit for sidewalk work, and others may not. Setback and right-of-way requirements can vary. We recommend checking with your local building department before the project begins, and we can help point you in the right direction."
      }
    },
    {
      "@type": "Question",
      "name": "Do you have to put gravel under a concrete sidewalk?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Gravel creates a stable base and allows water to drain away from the slab. Without it, moisture collects beneath the concrete. In North Jersey, that moisture freezes and expands during winter, which pushes the slab upward and causes cracking."
      }
    },
    {
      "@type": "Question",
      "name": "Can you pour concrete directly on dirt?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You can, but we don't recommend it. Dirt holds moisture and shifts with seasonal temperature changes. A sidewalk poured on bare soil is more likely to crack and settle unevenly within the first couple of years."
      }
    },
    {
      "@type": "Question",
      "name": "What is the best thickness for a concrete sidewalk?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "That depends on the intended use and soil conditions. A qualified contractor will evaluate your property and recommend the appropriate thickness. Going too thin to save on materials often leads to early failure and a more expensive replacement down the road."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need rebar in my concrete sidewalk?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Rebar or wire mesh reinforces the slab and helps prevent cracking. Whether your sidewalk requires rebar depends on the length, soil conditions, and expected use. Our crews evaluate each project and include reinforcement where it's needed."
      }
    },
    {
      "@type": "Question",
      "name": "What is the 90 minute rule for concrete?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Once concrete is mixed, it must be poured and finished within 90 minutes. After that window, the mix begins to set and becomes difficult to work with. Our crews plan every pour so the timing stays on track from delivery to finish."
      }
    },
    {
      "@type": "Question",
      "name": "How long before I can walk on new concrete?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most sidewalks can handle light foot traffic within 24 to 48 hours. Full cure takes approximately 28 days. We recommend avoiding heavy loads during that period so the concrete can reach its full strength."
      }
    }
  ]
};

const faqs = [
  {
    question: "How long does concrete sidewalk installation take in North Jersey?",
    answer: "Most residential sidewalks take 2 to 5 days to complete. That includes excavation, base prep, forming, pouring, and cure time. Weather can also play a role, especially if we're working around rain in Bergen or Essex counties."
  },
  {
    question: "Do I need a permit for a concrete sidewalk in North New Jersey?",
    answer: "It depends on your municipality. Some towns require a permit for sidewalk work, and others may not. Setback and right-of-way requirements can vary. We recommend checking with your local building department before the project begins, and we can help point you in the right direction."
  },
  {
    question: "Do you have to put gravel under a concrete sidewalk?",
    answer: "Yes. Gravel creates a stable base and allows water to drain away from the slab. Without it, moisture collects beneath the concrete. In North Jersey, that moisture freezes and expands during winter, which pushes the slab upward and causes cracking."
  },
  {
    question: "Can you pour concrete directly on dirt?",
    answer: "You can, but we don't recommend it. Dirt holds moisture and shifts with seasonal temperature changes. A sidewalk poured on bare soil is more likely to crack and settle unevenly within the first couple of years."
  },
  {
    question: "What is the best thickness for a concrete sidewalk?",
    answer: "That depends on the intended use and soil conditions. A qualified contractor will evaluate your property and recommend the appropriate thickness. Going too thin to save on materials often leads to early failure and a more expensive replacement down the road."
  },
  {
    question: "Do I need rebar in my concrete sidewalk?",
    answer: "Rebar or wire mesh reinforces the slab and helps prevent cracking. Whether your sidewalk requires rebar depends on the length, soil conditions, and expected use. Our crews evaluate each project and include reinforcement where it's needed."
  },
  {
    question: "What is the 90 minute rule for concrete?",
    answer: "Once concrete is mixed, it must be poured and finished within 90 minutes. After that window, the mix begins to set and becomes difficult to work with. Our crews plan every pour so the timing stays on track from delivery to finish."
  },
  {
    question: "How long before I can walk on new concrete?",
    answer: "Most sidewalks can handle light foot traffic within 24 to 48 hours. Full cure takes approximately 28 days. We recommend avoiding heavy loads during that period so the concrete can reach its full strength."
  }
];

const citations = [
  {
    num: 1,
    text: "American Concrete Institute (ACI), ACI 318: Building Code Requirements for Structural Concrete – Standards for base preparation and structural concrete requirements.",
    url: "https://www.concrete.org"
  },
  {
    num: 2,
    text: "ASTM International, ASTM C94: Standard Specification for Ready-Mixed Concrete – Requirements for ready-mix concrete batching, mixing, and delivery.",
    url: "https://www.astm.org"
  },
  {
    num: 3,
    text: "ASTM International, ASTM C150: Standard Specification for Portland Cement – Cement standards for concrete mix design.",
    url: "https://www.astm.org"
  },
  {
    num: 4,
    text: "New Jersey Uniform Construction Code, Residential Code Table R301.2 – Frost line depth for Northern New Jersey.",
    url: "https://www.nj.gov/dca/divisions/codes"
  },
  {
    num: 5,
    text: "National Association of Realtors, 2023 Remodeling Impact Report – Exterior improvements and buyer interest.",
    url: "https://www.nar.realtor"
  },
  {
    num: 6,
    text: "American Concrete Institute (ACI), ACI 332: Residential Code Requirements for Structural Concrete – Residential concrete standards including the 90 minute rule for ready-mix delivery and placement.",
    url: "https://www.concrete.org"
  }
];

export default function ConcreteSidewalks() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Concrete Sidewalk Installation Totowa, NJ | Masonry Pro</title>
        <meta property="og:description" content="Cracked sidewalk becoming a safety issue? We pour concrete sidewalks the right way in North Jersey. Proper base, rebar, and control joints on every project." />
        <meta property="og:title" content="Concrete Sidewalk Installation Totowa, NJ | Masonry Pro" />
        <meta 
          name="description" 
          content="Cracked sidewalk becoming a safety issue? We pour concrete sidewalks the right way in North Jersey. Proper base, rebar, and control joints on every project." 
        />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Concrete Sidewalk Installation",
            description: "Concrete sidewalk installation in North Jersey with proper base, rebar, and control joints.",
            url: "/concrete-services/sidewalks",
            image: "https://masonry-pro.com/og/concrete-sidewalks.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/concrete-sidewalks.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/concrete-sidewalks.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Concrete sidewalk installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Concrete Sidewalk Installation in Totowa, NJ Serving North Jersey: <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Safe and Lasting Walkways</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  A concrete sidewalk must be poured the right way to hold up over time. We install and replace sidewalks for homeowners across Passaic, Bergen, Essex, and Morris counties. Our crews follow <a href="https://www.concrete.org" target="_blank" rel="noopener noreferrer" style={{ color: '#d4a853' }}>ACI standards</a><CitationLink num={1} url="https://www.concrete.org" /> on every pour. Whether you're replacing a cracked path or adding a new one, we build sidewalks that can handle our North Jersey weather for decades.
                </p>
              </div>
            </div>
          </section>

          {/* Content Sections */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                {/* Proper Base Section */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A Proper Base Prevents <span className="text-gradient-gold">Cracking and Settling</span>
                  </h2>
                  <figure className="mb-6 lg:mb-4 lg:w-1/2 lg:float-left lg:mr-8">
                    <img
                      src={baseImage}
                      alt="Compacted gravel base and wood forms set for a concrete sidewalk installation in Totowa, NJ"
                      width={1200}
                      height={864}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto rounded-xl border border-border"
                    />
                  </figure>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We place compacted gravel beneath every sidewalk we pour. The gravel creates a level surface and gives water somewhere to go. Without it, moisture sits directly under the slab.
                    </p>
                    <p>
                      Homeowners sometimes ask if we can pour directly on dirt. We don't do it that way. Dirt shifts when it gets wet, and it moves even more when temperatures change. In North Jersey, that kind of movement can crack a new sidewalk within a year or two.
                    </p>
                    <p>
                      Here's what happens when there's no gravel underneath. Water collects under the slab. It freezes in winter and expands. The concrete gets pushed up from below. Then you end up with cracks and uneven sections that only get worse.
                    </p>
                    <p>
                      Clay soil shows up all over Essex and Bergen counties. That type of soil holds a lot of moisture, which makes the problem worse. We lay gravel down to create separation between the clay and the concrete. ACI has standards for base preparation depending on soil type and how the sidewalk will be used.<CitationLink num={1} url="https://www.concrete.org" /> Those are the standards we follow.
                    </p>
                  </div>
                  <div className="clear-both" />
                </div>

                {/* Ground Preparation Section */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Ground Preparation Makes the Difference in <span className="text-gradient-gold">Sidewalk Longevity</span>
                  </h2>
                  <figure className="mb-6 lg:mb-4 lg:w-1/2 lg:float-right lg:ml-8">
                    <img
                      src={formsImage}
                      alt="Freshly poured concrete sidewalk section with forms and rebar stakes in place in Totowa, NJ"
                      width={720}
                      height={480}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto rounded-xl border border-border"
                    />
                  </figure>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We begin by digging out the path where your sidewalk will go. The depth depends on the soil conditions and drainage requirements. That gives us room for the gravel base and the concrete on top.
                    </p>
                    <p>
                      The ground is graded to direct water away from your home and off the surface. Even a slight pitch makes a difference. Water that pools on a sidewalk can freeze in winter and break down the surface over time.
                    </p>
                    <p>
                      We set forms along both sides to hold the concrete in place while it cures. The forms must be level and secure. If they shift during the pour, the finished sidewalk won't be straight or even.
                    </p>
                    <p>
                      Control joints are cut into the surface at regular intervals. These aren't cracks. They're planned lines that give the concrete a place to expand and contract with temperature changes. Without them, the slab cracks wherever it wants.
                    </p>
                    <p>
                      Every property is a little different. Soil type, slope, and proximity to trees all factor in. We evaluate conditions before starting and adjust our prep work to match what we're dealing with.
                    </p>
                  </div>
                  <div className="clear-both" />
                </div>

                {/* Year-Round Installation Section */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    We Install Concrete Sidewalks <span className="text-gradient-gold">Year-Round</span> in North Jersey
                  </h2>
                  <figure className="mb-6 lg:mb-4 lg:w-1/2 lg:float-left lg:mr-8">
                    <img
                      src={winterPourImage}
                      alt="Concrete sidewalk slabs curing in winter conditions with control joints cut and forms still set in Totowa, NJ"
                      width={1200}
                      height={900}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto rounded-xl border border-border"
                    />
                  </figure>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We pour sidewalks throughout the year. Each season has its own advantages, and we adjust our approach based on the conditions. The ground must be workable, and the temperature must be right for the concrete to cure properly.
                    </p>
                    <p>
                      Homeowners in West Orange and Hackensack often schedule their sidewalk projects before summer so everything is set for the warmer months. Others book in fall or winter when contractor schedules tend to open up. Either approach works.
                    </p>
                    <p>
                      Concrete cures best when temperatures remain between 50 and 80 degrees. When we're working outside that range, we take additional steps. In colder weather, we can use insulated blankets to protect the fresh pour. The project doesn't stop. We simply adjust our methods.
                    </p>
                    <p>
                      The 90 minute rule is something worth mentioning. Once concrete is mixed, it must be poured and finished within 90 minutes.<CitationLink num={6} url="https://www.concrete.org" /> That's an industry standard we follow regardless of the season. Our crews plan each pour so the timing stays on track from start to finish.
                    </p>
                    <p>
                      If you're in Paramus, <Link to="/service-areas/passaic-county/clifton" className="text-secondary hover:underline">Clifton</Link>, or Wayne and considering a new sidewalk, give us a call or text. We can discuss timing that works with your schedule.
                    </p>
                  </div>
                  <div className="clear-both" />
                </div>

                {/* Longevity Section */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A Well-Poured Sidewalk Lasts <span className="text-gradient-gold">Decades</span> in Our Climate
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A concrete sidewalk is a long-term investment. When poured correctly, it can last 25 to 50 years. That's the kind of return homeowners in Livingston and Short Hills are looking for.
                    </p>
                    <p>
                      The strength of the concrete itself matters. We use ready-mix concrete that meets <a href="https://www.astm.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ASTM C94 specifications</a>.<CitationLink num={2} url="https://www.astm.org" /> The mix is designed for the right balance of strength, workability, and durability. Rebar or wire mesh gets placed inside the forms before the pour to reinforce the slab from within.
                    </p>
                    <p>
                      Control joints help manage how the concrete moves with temperature changes. Without control joints, cracks form randomly and spread over time. With proper joint spacing, the concrete can expand and contract without causing visible damage.
                    </p>
                    <p>
                      Our winters here in North Jersey can take a toll on outdoor surfaces. Freeze-thaw cycles are relentless. Water seeps into small cracks, freezes, and then expands. That cycle breaks down poorly poured sidewalks over time. A proper base, quality mix, and correct finishing techniques can prevent that from happening. ACI-standard practices are what separate a sidewalk that lasts from one that doesn't.<CitationLink num={1} url="https://www.concrete.org" />
                    </p>
                  </div>
                </div>

                {/* Safety and Curb Appeal Section */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A New Sidewalk Adds Safety and <span className="text-gradient-gold">Curb Appeal</span> to Your Property
                  </h2>
                  <figure className="mb-6 lg:mb-4 lg:w-1/2 lg:float-right lg:ml-8">
                    <img
                      src={finishedImage}
                      alt="Finished curved concrete sidewalk and walkway at a North Jersey home in Totowa, NJ"
                      width={2320}
                      height={1740}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto rounded-xl border border-border"
                    />
                  </figure>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A cracked or uneven sidewalk is more than an eyesore. It can be a safety concern. If someone trips suddenly, it's a problem you wish you had addressed sooner.
                    </p>
                    <p>
                      A new concrete sidewalk makes your property look well-maintained. It's one of the first things people notice when they walk up to your home. In neighborhoods like Glen Ridge and Maplewood, that kind of curb appeal can make a real difference.
                    </p>
                    <p>
                      The <a href="https://www.nar.realtor" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Association of Realtors</a> puts exterior improvements high on the list of features that buyers pay attention to.<CitationLink num={5} url="https://www.nar.realtor" /> A clean, level sidewalk signals that the property has been cared for. Buyers shopping in Bergen and Essex counties often factor in the condition of walkways and driveways when making their decision.
                    </p>
                    <p>
                      Beyond resale, your family uses the sidewalk every day. Walking to the car, bringing in groceries, kids riding bikes. A level, solid surface makes all of that safer and easier. That's the kind of value that goes beyond what shows up on an appraisal.
                    </p>
                  </div>
                  <div className="clear-both" />
                </div>

                {/* Common Mistakes Section */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Common Sidewalk Installation <span className="text-gradient-gold">Mistakes</span> We See Across North Jersey
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Contractors who rush through a sidewalk pour tend to cut corners. We get calls from homeowners in Wayne, Paramus, and other towns asking us to fix concrete that wasn't done right the first time.
                    </p>
                    <p>
                      No gravel base underneath is the biggest one. We've repaired sidewalks that were only a couple of seasons old because they were poured directly on dirt. The slab settled unevenly, and cracks spread from there.
                    </p>
                    <p>
                      Some contractors pour the slab too thin to save on materials. That doesn't hold up. A residential sidewalk must be thick enough to handle foot traffic and seasonal ground movement. Going too thin ends up costing more when the slab fails and needs to be torn out.
                    </p>
                    <p>
                      Control joints are something else we see done wrong. Either they're skipped entirely or spaced too far apart. Without proper joint placement, the concrete cracks wherever it wants instead of along the planned lines.
                    </p>
                    <p>
                      Weather during the pour matters too. Concrete that cures too fast in extreme heat won't reach full strength. Same goes for cold weather if the slab isn't protected. Once the concrete is mixed, there's a 90 minute window to get it poured and finished.<CitationLink num={6} url="https://www.concrete.org" /> Our crews plan each pour around those conditions.
                    </p>
                  </div>
                </div>

                {/* Featured Snippet Section */}
                <div className="mb-16 p-8 bg-muted/50 rounded-xl border border-border">
                  <h3 className="text-2xl font-bold mb-4">How long does a concrete sidewalk last?</h3>
                  <p className="text-lg text-muted-foreground mb-4">
                    A properly poured concrete sidewalk can last 25 to 50 years. In Northern New Jersey, our freeze-thaw cycles can take a toll on outdoor surfaces, so how the sidewalk is built makes a real difference.
                  </p>
                  <p className="text-lg font-semibold mb-3">Factors that affect how long your sidewalk will last:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>A compacted gravel base appropriate for your soil conditions, per ACI standards<CitationLink num={1} url="https://www.concrete.org" /></li>
                    <li>Reinforcement with rebar or wire mesh inside the slab</li>
                    <li>Properly spaced control joints to manage expansion and contraction</li>
                    <li>Ready-mix concrete that meets ASTM C94 specifications<CitationLink num={2} url="https://www.astm.org" /></li>
                  </ul>
                  <p className="text-lg text-muted-foreground mt-4">
                    When a qualified masonry contractor handles the base prep, reinforcement, and finishing, your sidewalk can hold up for decades in our regional climate.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <YouTubeShortsSection 
            title="See Our"
            highlightedTitle="Work in Action"
            videoIds={["YBv-bvrkguI"]}
          />

          {/* Category hub backlink */}
          <section className="py-8 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-muted-foreground">
                  Sidewalks are one of the many <Link to="/concrete-services" className="text-secondary hover:underline">concrete projects we handle in North Jersey</Link>, from driveways to foundations.
                </p>
                <p className="text-muted-foreground mt-4">
                  When a walkway is only cracked or settled in spots, <Link to="/concrete-services/repair" className="text-secondary hover:underline">concrete repair</Link> is often the cheaper fix before a full replacement.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-background p-6 rounded-xl border border-border">
                    <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Citations Section */}
          <section className="py-12 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground">
                        {citations.map((citation) => (
                          <li key={citation.num} className="flex gap-2">
                            <span className="font-semibold">[{citation.num}]</span>
                            <span>
                              {citation.text}{" "}
                              <a
                                href={citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                              >
                                {citation.url}
                              </a>
                            </span>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="concrete" />

          {/* CTA Section */}
          <CTASection serviceName="Concrete Sidewalk" heading="Ready for a New" highlightedHeading="Sidewalk?" />
        </main>

        <Footer />
      </div>
    </>
  );
}
